/**
 * What each shift is at month close (plan 0026, "P1, Timeliste as the month-close workbench").
 * One function decides, so the queue, the matrix, the counts and the bulk rule cannot disagree.
 *
 * Only a finished shift can need anything: a future shift is not missing a punch, and an open
 * punch on a shift still running is somebody at work.
 */
import type { ApiShift } from '@/api/data';
import { T } from '@/i18n';

export type Kind = 'future' | 'running' | 'missing' | 'open' | 'deviation' | 'ready' | 'approved';

const at = (iso: string | null | undefined): number => (iso ? Date.parse(iso) : Number.NaN);
const minutes = (ms: number) => Math.round(Math.abs(ms) / 60000);
/** "52 min"; past two hours "3 t 27 min", since nobody reads +1438 min at a glance. */
export const dur = (m: number) =>
  m > 120 ? `${Math.floor(m / 60)} t${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`;

/**
 * A shift worked under recorded breaks (its own break_mode, plan 0032) that ran past 5.5 h with no
 * break punched: the manager decides before approving. Under punched_min the automatic minimum comes
 * off anyway, and the words say so. Automatic shifts have nothing to punch.
 */
export function noBreak(s: ApiShift): boolean {
  if (s.break_mode !== 'punched' && s.break_mode !== 'punched_min') return false;
  if (!s.punch_in_time || !s.punch_out_time || (s.lunch_in_time && s.lunch_out_time)) return false;
  return at(s.punch_out_time) - at(s.punch_in_time) > 5.5 * 3_600_000;
}
const noBreakText = (s: ApiShift) =>
  s.break_mode === 'punched_min'
    ? T('Ingen pause registrert, minimumspausen trekkes')
    : T('Ingen pause registrert');

export function kindOf(s: ApiShift, now: number, toleranceMin = 15): Kind {
  if (s.is_approved) return 'approved';
  const start = at(s.start_time);
  const end = at(s.end_time);
  const ended = Number.isFinite(end) && end < now;
  if (!ended) return Number.isFinite(start) && start > now ? 'future' : 'running';
  if (!s.punch_in_time) return 'missing';
  if (!s.punch_out_time) return 'open';
  const tol = toleranceMin * 60000;
  const off = Math.abs(at(s.punch_in_time) - start) > tol || Math.abs(at(s.punch_out_time) - end) > tol;
  return off || noBreak(s) ? 'deviation' : 'ready';
}

/** Shifts that need a person before the month can close. */
export const needsAction = (k: Kind) => k === 'missing' || k === 'open' || k === 'deviation';
/** Shifts a bulk approval may include: never a missing or open punch. */
export const approvable = (k: Kind) => k === 'ready' || k === 'deviation';

/**
 * The deviation in words, the larger of the two ends: "Stemplet inn 52 min før plan".
 * Null when the shift is not a deviation.
 */
export function deviationText(s: ApiShift, toleranceMin = 15): string | null {
  if (!s.punch_in_time || !s.punch_out_time) return null;
  const inD = at(s.punch_in_time) - at(s.start_time);
  const outD = at(s.punch_out_time) - at(s.end_time);
  const tol = toleranceMin * 60000;
  const useIn = Math.abs(inD) >= Math.abs(outD);
  const d = useIn ? inD : outD;
  const t = dur(minutes(d));
  const time =
    Math.abs(d) <= tol
      ? null
      : useIn
        ? d < 0
          ? T('Stemplet inn {time} før plan', { time: t })
          : T('Stemplet inn {time} etter plan', { time: t })
        : d < 0
          ? T('Stemplet ut {time} før plan', { time: t })
          : T('Stemplet ut {time} etter plan', { time: t });
  const brk = noBreak(s) ? noBreakText(s) : null;
  return time && brk ? `${time}. ${brk}` : (time ?? brk);
}

/**
 * The same deviation as a signed amount of working time, for a matrix cell: "+52 min" when the
 * shift ran longer than planned at the end that is further off, "−54 min" when shorter.
 */
export function deviationShort(s: ApiShift, toleranceMin = 15): string | null {
  if (!s.punch_in_time || !s.punch_out_time) return null;
  const inD = at(s.start_time) - at(s.punch_in_time); // early in is more work
  const outD = at(s.punch_out_time) - at(s.end_time); // late out is more work
  const d = Math.abs(inD) >= Math.abs(outD) ? inD : outD;
  // on time but no break punched: the cell names the break, not a few minutes either way
  if (Math.abs(d) <= toleranceMin * 60000 && noBreak(s)) return T('ingen pause');
  return `${d >= 0 ? '+' : '−'}${dur(minutes(d))}`;
}

export interface Counts {
  action: number;
  missing: number;
  open: number;
  deviation: number;
  ready: number;
  approved: number;
  ended: number;
}

export function countKinds(shifts: ApiShift[], now: number, toleranceMin = 15): Counts {
  const c: Counts = { action: 0, missing: 0, open: 0, deviation: 0, ready: 0, approved: 0, ended: 0 };
  for (const s of shifts) {
    if (s.user_id == null) continue;
    const k = kindOf(s, now, toleranceMin);
    if (k === 'future' || k === 'running') continue;
    c.ended++;
    if (k === 'approved') c.approved++;
    else c[k]++;
    if (needsAction(k)) c.action++;
  }
  return c;
}
